const themeColors = {
	accentBlue: "#228be6",
	accentBlueStrong: "#1c7ed6",
	accentBlueSoft: "#4dabf7",
	accentBlueRgb: "34, 139, 230",
	accentBlueSoftRgb: "77, 171, 247",
	accentPurple: "#7048e8",
	accentCyan: "#00bcd4",
	accentLive: "#e03131",
	accentNeutral: "#868e96",
	infoTint: "rgba(33, 150, 243, 0.125)",
	neutralTint: "rgba(158, 158, 158, 0.125)",
	selectedLight: "rgba(34, 139, 230, 0.12)",
	selectedLightStrong: "rgba(34, 139, 230, 0.18)",
	dropdownSelectedBgLight: "rgba(34, 184, 207, 0.42)",
	dropdownSelectedBgDark: "rgba(16, 152, 175, 0.58)",
	dropdownSelectedColorLight: "#0b7285",
	dropdownSelectedColorDark: "#ffffff"
};

// Single source of truth for opacity. Change a value here and every
// surface/border/overlay that references it (JS via config.theme.opacities
// or CSS via var(--app-opacity-*) / var(--app-surface-*)) updates at once.
const themeOpacities = {
	subtle: 0.05,  // faint surface fills (cards on a gradient hero)
	soft: 0.1,     // light surfaces / subtle borders
	muted: 0.2,    // borders on translucent surfaces
	medium: 0.3,   // chart area fills, overlays
	strong: 0.6    // emphasized overlays
};

// Translucent white surfaces derived from the opacities above.
const themeSurfaces = {
	overlaySubtle: `rgba(255, 255, 255, ${themeOpacities.subtle})`,
	overlaySoft: `rgba(255, 255, 255, ${themeOpacities.soft})`,
	overlayMuted: `rgba(255, 255, 255, ${themeOpacities.muted})`
};

const themeGradients = {
	primary: "linear-gradient(135deg, #3b82f6, #8b5cf6)",
	aurora: "linear-gradient(90deg, #3b82f6, #8b5cf6, #ec4899, #f59e0b, #3b82f6)",
	errorTitle: "linear-gradient(45deg, #228be6, #7048e8, #d6336c)",
	aiComposer: "linear-gradient(135deg, #667eea 0%, #764ba2 25%, #f093fb 50%, #4facfe 75%, #667eea 100%)",
	warm: "linear-gradient(135deg, #ec4899, #f59e0b)",
	cool: "linear-gradient(135deg, #06b6d4, #10b981)",
	storeHero: "linear-gradient(135deg, #228be6 0%, #1c7ed6 100%)"
};

const themeShadows = {
	cursorGlow: "0 0 12px rgba(59, 130, 246, 0.4)",
	gradientButtonLight: "0 8px 20px rgba(59,130,246,0.22), inset 0 1px 0 rgba(255,255,255,0.40)",
	gradientButtonDark: "0 8px 22px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.10)",
	aiComposerActive:
		"0 0 20px rgba(102, 126, 234, 0.4), 0 0 40px rgba(118, 75, 162, 0.3), 0 0 60px rgba(245, 147, 251, 0.2)",
	aiComposerActiveStrong:
		"0 0 30px rgba(102, 126, 234, 0.6), 0 0 50px rgba(118, 75, 162, 0.5), 0 0 70px rgba(245, 147, 251, 0.4), 0 0 90px rgba(79, 172, 254, 0.3)"
};

export const themeSettings = {
	colors: themeColors,
	opacities: themeOpacities,
	surfaces: themeSurfaces,
	gradients: themeGradients,
	shadows: themeShadows,
	cssVariables: {
		"--app-opacity-subtle": String(themeOpacities.subtle),
		"--app-opacity-soft": String(themeOpacities.soft),
		"--app-opacity-muted": String(themeOpacities.muted),
		"--app-opacity-medium": String(themeOpacities.medium),
		"--app-opacity-strong": String(themeOpacities.strong),
		"--app-surface-overlay-subtle": themeSurfaces.overlaySubtle,
		"--app-surface-overlay-soft": themeSurfaces.overlaySoft,
		"--app-surface-overlay-muted": themeSurfaces.overlayMuted,
		"--app-accent-blue": themeColors.accentBlue,
		"--app-accent-blue-strong": themeColors.accentBlueStrong,
		"--app-accent-blue-soft": themeColors.accentBlueSoft,
		"--app-accent-blue-rgb": themeColors.accentBlueRgb,
		"--app-accent-blue-soft-rgb": themeColors.accentBlueSoftRgb,
		"--app-accent-purple": themeColors.accentPurple,
		"--app-accent-cyan": themeColors.accentCyan,
		"--app-accent-live": themeColors.accentLive,
		"--app-accent-neutral": themeColors.accentNeutral,
		"--app-info-tint": themeColors.infoTint,
		"--app-neutral-tint": themeColors.neutralTint,
		"--app-selected-light": themeColors.selectedLight,
		"--app-selected-light-strong": themeColors.selectedLightStrong,
		"--app-dropdown-selected-bg-light": themeColors.dropdownSelectedBgLight,
		"--app-dropdown-selected-bg-dark": themeColors.dropdownSelectedBgDark,
		"--app-dropdown-selected-color-light": themeColors.dropdownSelectedColorLight,
		"--app-dropdown-selected-color-dark": themeColors.dropdownSelectedColorDark,
		"--app-gradient-primary": themeGradients.primary,
		"--app-gradient-aurora": themeGradients.aurora,
		"--app-gradient-error-title": themeGradients.errorTitle,
		"--app-gradient-ai-composer": themeGradients.aiComposer,
		"--app-gradient-warm": themeGradients.warm,
		"--app-gradient-cool": themeGradients.cool,
		"--app-gradient-store-hero": themeGradients.storeHero,
		"--app-shadow-cursor-glow": themeShadows.cursorGlow,
		"--app-shadow-gradient-button-light": themeShadows.gradientButtonLight,
		"--app-shadow-gradient-button-dark": themeShadows.gradientButtonDark,
		"--app-shadow-ai-composer": themeShadows.aiComposerActive,
		"--app-shadow-ai-composer-strong": themeShadows.aiComposerActiveStrong
	}
};

export function applyThemeCssVariables(target) {
	const root = target || (typeof document !== "undefined" ? document.documentElement : null);
	if (!root) return;

	Object.entries(themeSettings.cssVariables).forEach(([property, value]) => {
		root.style.setProperty(property, value);
	});
}

export function getGradientBorderButtonStyles(colorScheme) {
	const overlayBackground = colorScheme === "dark"
		? "linear-gradient(165deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.05) 55%, rgba(255,255,255,0.02) 100%)"
		: "linear-gradient(165deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.82) 55%, rgba(255,255,255,0.72) 100%)";
	const hoverShadow = colorScheme === "dark"
		? "var(--app-shadow-gradient-button-dark)"
		: "var(--app-shadow-gradient-button-light)";

	return `
		@keyframes gradientMove {
			0% { background-position: 0% 50%; }
			100% { background-position: 300% 50%; }
		}

		.gradient-border-button {
			position: relative;
			background: transparent;
			border: 1px solid transparent;
			overflow: visible;
			transition: all 0.3s ease;
		}

		.gradient-border-button::before {
			content: "";
			position: absolute;
			top: -2px;
			left: -2px;
			right: -2px;
			bottom: -2px;
			border-radius: 10px;
			background: var(--app-gradient-aurora);
			background-size: 300% 100%;
			z-index: -1;
			opacity: 0;
			transition: opacity 0.3s ease;
		}

		.gradient-border-button::after {
			content: "";
			position: absolute;
			top: 0;
			left: 0;
			right: 0;
			bottom: 0;
			border-radius: 8px;
			background: transparent;
			z-index: -1;
			opacity: 0;
			transition: opacity 0.3s ease;
		}

		.gradient-border-button:hover::before,
		.gradient-border-button:focus-visible::before,
		.gradient-border-button:active::before,
		.gradient-border-button[data-selected="true"]::before {
			opacity: 1;
			animation: gradientMove 2s linear infinite;
		}

		.gradient-border-button:hover::after,
		.gradient-border-button:focus-visible::after,
		.gradient-border-button:active::after,
		.gradient-border-button[data-selected="true"]::after {
			opacity: 1;
			background: ${overlayBackground};
			backdrop-filter: blur(10px);
			-webkit-backdrop-filter: blur(10px);
		}

		.gradient-border-button:hover,
		.gradient-border-button:focus-visible,
		.gradient-border-button:active,
		.gradient-border-button[data-selected="true"] {
			border-color: transparent;
			transform: scale(1.02);
			box-shadow: ${hoverShadow};
		}
	`;
}
